import type {
  ArenaChatInboxItem,
  ArenaChatThread,
  ArenaInboxItem,
  ArenaInboxItemKind,
  ArenaMatchChatMessage,
  ArenaMatchChatReadState,
  ArenaMatchFinancialStatus,
  ArenaMatchLobby,
  ArenaOpenChallenge,
} from "@/types/services/api-contracts";

import { callEdgeData } from "./_core/edge-call";
import { ARENA_FUNCTION_NAME } from "./_core/function-names";

export const arenaMatchService = {
  async getArenaInbox(): Promise<ArenaInboxItem[]> {
    return callEdgeData<ArenaInboxItem[]>(ARENA_FUNCTION_NAME, "get-arena-inbox", {
      method: "GET",
    });
  },

  async listMatchInbox(): Promise<ArenaChatInboxItem[]> {
    return callEdgeData<ArenaChatInboxItem[]>(ARENA_FUNCTION_NAME, "list-match-inbox", {
      method: "GET",
    });
  },

  async listMatchChat(matchId: string): Promise<ArenaChatThread> {
    return callEdgeData<ArenaChatThread>(ARENA_FUNCTION_NAME, "list-match-chat", {
      method: "GET",
      params: { match_id: matchId },
    });
  },

  async getMatchLobby(matchId: string): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "get-match-lobby", {
      method: "GET",
      params: { match_id: matchId },
    });
  },

  async getMatchFinancialStatus(matchId: string): Promise<ArenaMatchFinancialStatus | null> {
    return callEdgeData<ArenaMatchFinancialStatus | null>(ARENA_FUNCTION_NAME, "get-match-financial-status", {
      method: "GET",
      params: { match_id: matchId },
    });
  },

  async resumeMatchCheckout(data: {
    match_id: string;
    group_id?: string;
    return_platform?: "web" | "mobile";
  }): Promise<ArenaMatchFinancialStatus | null> {
    return callEdgeData<ArenaMatchFinancialStatus | null>(ARENA_FUNCTION_NAME, "resume-match-checkout", {
      body: data,
    });
  },

  async cancelMatchCheckout(data: {
    match_id: string;
    group_id?: string;
    reason?: string;
  }): Promise<ArenaMatchFinancialStatus | null> {
    return callEdgeData<ArenaMatchFinancialStatus | null>(ARENA_FUNCTION_NAME, "cancel-match-checkout", {
      body: data,
    });
  },

  async markFinancialOwner(data: {
    arena_open_challenge_id: string;
    financial_owner_group_id?: string | null;
    financial_owner_user_id?: string | null;
  }): Promise<ArenaOpenChallenge | null> {
    return callEdgeData<ArenaOpenChallenge | null>(ARENA_FUNCTION_NAME, "mark-financial-owner", {
      body: data,
    });
  },

  async syncMatchFinancialState(data: {
    match_id: string;
    group_id?: string | null;
    payment_id?: string | null;
    session_id?: string | null;
  }): Promise<ArenaMatchFinancialStatus | null> {
    return callEdgeData<ArenaMatchFinancialStatus | null>(ARENA_FUNCTION_NAME, "sync-match-financial-state", {
      body: data,
    });
  },

  async listMatchAttendance(matchId: string): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "list-match-attendance", {
      method: "GET",
      params: { match_id: matchId },
    });
  },

  async inviteMatchPlayer(data: {
    match_id: string;
    user_id: string;
    group_id?: string;
  }): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "invite-match-player", {
      body: data,
    });
  },

  async respondMatchAttendance(data: {
    match_id: string;
    status: "confirmed" | "unavailable";
    group_id?: string;
  }): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "respond-match-attendance", {
      body: data,
    });
  },

  async lockMatchRoster(data: {
    match_id: string;
    group_id?: string;
  }): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "lock-match-roster", {
      body: data,
    });
  },

  async saveMatchLineup(data: {
    match_id: string;
    group_id: string;
    players: Array<{
      user_id: string;
      slot_order?: number;
      role?: "starter" | "reserve";
    }>;
    submit?: boolean;
  }): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "save-match-lineup", {
      body: data,
    });
  },

  async getMatchdayStatus(matchId: string): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "get-matchday-status", {
      method: "GET",
      params: { match_id: matchId },
    });
  },

  async listMatchCheckins(matchId: string): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "list-match-checkins", {
      method: "GET",
      params: { match_id: matchId },
    });
  },

  async checkInMatchPlayer(data: {
    match_id: string;
    group_id?: string;
  }): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "check-in-match-player", {
      body: data,
    });
  },

  async markMatchPlayerAbsent(data: {
    match_id: string;
    group_id?: string;
  }): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "mark-match-player-absent", {
      body: data,
    });
  },

  async captainConfirmMatchPlayer(data: {
    match_id: string;
    user_id: string;
    group_id?: string;
  }): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "captain-confirm-match-player", {
      body: data,
    });
  },

  async evaluateMatchdayReadiness(data: {
    match_id: string;
  }): Promise<ArenaMatchLobby> {
    return callEdgeData<ArenaMatchLobby>(ARENA_FUNCTION_NAME, "evaluate-matchday-readiness", {
      body: data,
    });
  },

  async sendMatchMessage(data: {
    match_id: string;
    message: string;
    client_message_id?: string;
    reply_to_message_id?: string | null;
  }): Promise<ArenaMatchChatMessage> {
    return callEdgeData<ArenaMatchChatMessage>(ARENA_FUNCTION_NAME, "send-match-message", {
      body: data,
    });
  },

  async markMatchChatRead(data: {
    match_id: string;
    message_id?: string | null;
  }): Promise<ArenaMatchChatReadState & { match_id: string }> {
    return callEdgeData<ArenaMatchChatReadState & { match_id: string }>(
      ARENA_FUNCTION_NAME,
      "mark-match-chat-read",
      {
        body: data,
      },
    );
  },

  async markArenaInboxItemSeen(data: {
    kind: ArenaInboxItemKind;
    match_id?: string | null;
  }): Promise<{ success: boolean }> {
    return callEdgeData<{ success: boolean }>(ARENA_FUNCTION_NAME, "mark-arena-inbox-item-seen", {
      body: data,
    });
  },
};
